/*
This directive forward the HTML validityState (https://developer.mozilla.org/en-US/docs/Web/API/ValidityState) to the expression it's bound to
It hooks itself to the input event or the v-model changes if v-model is used

Usage:

for simple validation just pass it to the component that renders the input
<input v-validity="state" v-model="value"  required min-lenght="10" />

For group validation you can use a form element like so:
<form v-validity="state">
  <input type="text" required min-lenght="10" v-model="textValue" />
  <input type="checkbox" required v-model="radioValue" />
</fom>

You could also just query the valid state with the v-valid directive

Usage:
<input v-valid="isValid" v-model="value"  required min-lenght="10" />

*/

import Vue from 'vue';

const cases = [
  'badInput',
  'patternMismatch',
  'rangeOverflow',
  'rangeUnderflow',
  'stepMismatch',
  'tooLong',
  'tooShort',
  'typeMismatch',
  'valueMissing',
];

// store for all the handlers and contexts
const store = new WeakMap();

const isInputElement = (el) => (
  el instanceof HTMLInputElement ||
    el instanceof HTMLSelectElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLFormElement
);

const findInput = (el) => {
  if (isInputElement(el)) {
    return el;
  }
  return el.querySelector('input, select');
};

const updateValidity = (el) => {
  const element = store.get(el);
  // If the element doesn't exist anymore (has been removed), do not try to update its validity
  if (!element) {
    return;
  }
  const { context, expression, inputEl, onlyValid } = element;
  const { validity } = inputEl;
  const valid = inputEl.checkValidity();
  const oldValid = onlyValid ? context[expression] : context[expression].valid;

  // don't trigger an update if the validity is the same
  if (oldValid === valid) {
    return;
  }

  if (onlyValid) {
    context[expression] = valid;
    return;
  }

  const errors = validity ? cases.filter((errorCase) => validity[errorCase]) : null;
  context[expression] = { valid, errors };
};

const createHandler = ({ el, binding, vnode, ...rest }) => {
  const { expression } = binding;
  const { context } = vnode;
  const inputEl = findInput(el);

  store.set(el, { context, expression, inputEl, ...rest });
  // update initial validity
  updateValidity(el);
};

const updateHandler = ({ el, binding, vnode }) => {
  const { expression } = binding;
  const { context } = vnode;

  if (!store.has(el)) {
    return;
  }

  store.set(el, { ...store.get(el), expression, context });
  // the update handler is called before the elements are actually updated
  context.$nextTick(() => updateValidity(el));
};

const deleteHandler = ({ el }) => {
  store.delete(el);
};

// the validity directive
const validity = {
  inserted(el, binding, vnode) {
    createHandler({ el, binding, vnode });
  },
  componentUpdated(el, binding, vnode) {
    updateHandler({ el, binding, vnode });
  },
  unbind(el, binding, vnode) {
    deleteHandler({ el, binding, vnode });
  },
};

// the valid directive
export const valid = {
  inserted(el, binding, vnode) {
    createHandler({ el, binding, vnode, onlyValid: true });
  },
  componentUpdated(el, binding, vnode) {
    updateHandler({ el, binding, vnode, onlyValid: true });
  },
  unbind(el, binding, vnode) {
    deleteHandler({ el, binding, vnode, onlyValid: true });
  },
};

Vue.directive('validity', validity);
Vue.directive('valid', valid);

export default validity;
